import { useEffect, useRef, useState } from 'react'

/**
 * Inline issue-accordion state for a detail host (SiteDetail / HodDetail): one expanded row at a
 * time, Esc-collapse that fires before GisViewPage's overlay-clearing Esc, and the same
 * scroll-into-view + focus wiring the Issues board uses (see IssuesListBody). Hosts mark each row
 * wrapper with `data-issue-row={id}` (toggle button carrying `aria-expanded`) and render
 * IssueInlineDetail (which carries `data-issue-detail={id}`) as its sibling; `rootRef` goes on the
 * list wrapper so the querySelectors stay scoped to this host.
 */
export function useInlineIssueAccordion(rowIds: string[]): {
  expandedId: string | null
  toggle: (id: string) => void
  collapse: () => void
  rootRef: React.RefObject<HTMLDivElement | null>
} {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // Remembers the row toggle to restore focus on collapse; prevExpandedRef tracks the previous
  // expandedId so the trigger is only (re)captured on a genuine open.
  const triggerRef = useRef<HTMLElement | null>(null)
  const prevExpandedRef = useRef<string | null>(null)

  // Auto-collapse when the expanded issue leaves the host's list (refetch resolved it, filters
  // dropped it) — mirrors the board's behavior in IssuesDashboard.
  useEffect(() => {
    if (expandedId && !rowIds.includes(expandedId)) {
      setExpandedId(null)
    }
  }, [rowIds, expandedId])

  // Esc collapses the accordion FIRST. Capture phase on document + stopPropagation so GisViewPage's
  // bubble-phase handler (which clears the whole detail stack) never sees the event while we're
  // open; the photo lightbox still wins via its window-capture stopImmediatePropagation.
  useEffect(() => {
    if (!expandedId) {
      return
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return
      }
      if (e.target instanceof HTMLElement && e.target.closest('input,select,textarea')) {
        return
      }
      e.stopPropagation()
      setExpandedId(null)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [expandedId])

  // Bring the opened row into view + focus the detail region. Double-rAF so it runs after the
  // accordion mounts and the gis-accordion-in animation starts; the row (geometrically stable —
  // the detail grows below it) is what gets scrolled. On collapse, focus returns to the trigger.
  useEffect(() => {
    const prev = prevExpandedRef.current
    prevExpandedRef.current = expandedId

    if (!expandedId) {
      if (prev) {
        triggerRef.current?.focus()
        triggerRef.current = null
      }
      return
    }

    const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth'

    let raf1 = 0
    let raf2 = 0
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const root = rootRef.current
        if (!root) {
          return
        }
        const row = root.querySelector<HTMLElement>(`[data-issue-row="${expandedId}"]`)
        if (row && prev !== expandedId) {
          triggerRef.current = row.querySelector<HTMLElement>('button[aria-expanded]')
        }
        row?.scrollIntoView({ behavior, block: 'start' })
        root.querySelector<HTMLElement>(`[data-issue-detail="${expandedId}"]`)?.focus({ preventScroll: true })
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [expandedId])

  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id))
  const collapse = () => setExpandedId(null)

  return { expandedId, toggle, collapse, rootRef }
}
